import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AppointmentModal.css';
import { FaCircle, FaCheckCircle, FaPrint, FaExchangeAlt, FaPencilAlt, FaTrash, FaCheck, FaTimes, FaLock, FaInfoCircle } from 'react-icons/fa';
import { firestore } from '../../firebase';
import { collection, query, where, getDocs, setDoc, doc, getDoc, limit } from 'firebase/firestore';
import PrintableJobCard from '../PrintableJobCard/PrintableJobCard';
import ReactToPrint from 'react-to-print';
import { getTechnicians } from '../../utils/technicianUtils';
import { getLocationKey, getLocationSuffix } from '../../utils/location';
import { getTimetable } from '../../utils/timetable';
import { describeNonWorkingDay, isNonWorkingDay, parseDateInputValue } from '../../utils/workingDays';
import {
  PARTS_STATUS,
  PARTS_STATUS_OPTIONS,
  formatPartsTimestamp,
  getPartsStatus,
} from '../../utils/partsStatus';

const timeOptions = [
  { label: '30 minutes', value: 1 },
  { label: '1 hour', value: 2 },
  { label: '1.5 hours', value: 3 },
  { label: '2 hours', value: 4 },
  { label: '2.5 hours', value: 5 },
  { label: '3 hours', value: 6 },
  { label: '3.5 hours', value: 7 },
  { label: '4 hours', value: 8 },
  { label: '4.5 hours', value: 9 },
  { label: '5 hours', value: 10 },
  { label: '5.5 hours', value: 11 },
  { label: '6 hours', value: 12 },
  { label: '6.5 hours', value: 13 },
  { label: '7 hours', value: 14 },
  { label: '7.5 hours', value: 15 },
  { label: '8 hours', value: 16 },
];

// Define initial form data
const initialFormData = {
  vehicleReg: '',
  vehicleMake: '',
  customerName: '',
  customerPhone: '',
  expectedTime: 1,
  needsValidation: false, // New state for validation
  tasks: [],
  inProgress: false,
  newTasksAdded: false,
  startTime: null,
  pausedTime: null,
  resumeTime: null,
  totalPausedDuration: 0,
  totalTimeSpent: null,
  isPaused: false,
  comments: '',
  privateNote: '',
  technicianTimes: {}, // Stores the total time spent by each technician (keyed by technicianId)
  currentTechnicianId: null, // Track the currently active technician ID
  newCommentsAdded: false, // Include this field as well
  checkInTime: null, // To store the check-in time
  completionTime: null, // To store the completion time
  mileage: '', // New field for Mileage
  mileageUpdated: false, // Flag to track if mileage has been updated
  partsStatus: PARTS_STATUS.NONE, // Whether parts are needed/ordered/received
  partsOrderedAt: null, // When the parts were marked as ordered
  partsReceivedAt: null, // When the parts were marked as received
  partsStatusUpdatedBy: null, // Who last changed the parts status
  partsStatusUpdatedAt: null, // When the parts status was last changed
};

const FLAG_REASON_LABELS = {
  payment_overdue: 'payment overdue',
  payment_issues: 'payment issues',
  problematic_client: 'problematic client',
};

const getFlagReasonText = (flaggedReason) => {
  if (!flaggedReason) return '';
  return FLAG_REASON_LABELS[flaggedReason] || flaggedReason;
};

/**
 * The times an appointment can be moved to on a given day: the bookable slots
 * themselves, nothing else. Offering every half hour let a job be moved onto
 * lunch or onto 18:00, which are not slots - the calendar then could not place
 * it, because it looks the start time up in the day's slot list.
 * @param {Date|null} date - The day being moved to
 * @returns {Array<string>} Bookable start times
 */
const getRescheduleTimeOptions = (date) => (
  getTimetable(getLocationKey(), date instanceof Date ? date : new Date()).slots
);

const isValidRescheduleTime = (time, options) => options.includes(time);

const getSafeRescheduleTime = (time, options) => (
  isValidRescheduleTime(time, options) ? time : options[0]
);

function AppointmentModal({ appointment, onSave, onDelete, onClose, onCheckIn, startTime }) {

  const locationSuffix = getLocationSuffix();

  // Define the collection names
  const accountsCollectionName = 'accounts' + locationSuffix;

  const [formData, setFormData] = useState(initialFormData);
  const [newTask, setNewTask] = useState('');
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [editingTaskText, setEditingTaskText] = useState('');
  const [userRole, setUserRole] = useState('');
  const [username, setUsername] = useState('');
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false); // State for reschedule modal
  const [newTechnicianId, setNewTechnicianId] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [initialComments, setInitialComments] = useState('');
  const [showPrivateNote, setShowPrivateNote] = useState(false);
  // Customer notes live on the account, not the appointment, so they persist
  // across every booking for this vehicle reg.
  const [customerNotes, setCustomerNotes] = useState('');
  const [initialCustomerNotes, setInitialCustomerNotes] = useState('');
  const [showCustomerNotes, setShowCustomerNotes] = useState(false);
  const [vehicleRegLookupStatus, setVehicleRegLookupStatus] = useState('');
  const [isFlaggedAccount, setIsFlaggedAccount] = useState(false);
  const [flaggedReason, setFlaggedReason] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(true);


  const componentRef = useRef(); // Reference to the PrintableJobCard component
  const taskInputRef = useRef(null);
  const displayTime = appointment.id ? appointment.startTime : startTime;

  const loadAccountByVehicleReg = useCallback(async (vehicleReg) => {
    if (!vehicleReg) {
      return null;
    }

    const normalizedReg = vehicleReg.trim().toUpperCase().replace(/\s+/g, '');
    const byIdRef = doc(firestore, accountsCollectionName, normalizedReg);
    const byIdSnapshot = await getDoc(byIdRef);

    if (byIdSnapshot.exists()) {
      return {
        data: byIdSnapshot.data(),
        ref: byIdRef,
      };
    }

    const fallbackQuery = query(
      collection(firestore, accountsCollectionName),
      where('vehicleReg', '==', normalizedReg),
      limit(1)
    );
    const fallbackSnapshot = await getDocs(fallbackQuery);
    if (fallbackSnapshot.empty) {
      return null;
    }

    return {
      data: fallbackSnapshot.docs[0].data(),
      ref: fallbackSnapshot.docs[0].ref,
    };
  }, [accountsCollectionName]);

  // Mirror an account's flag + notes into the modal. Customer notes are opened
  // automatically when the account has any, so they are never skipped over.
  const applyAccountToForm = useCallback((accountData) => {
    const isFlagged = accountData?.flagged === true;
    setIsFlaggedAccount(isFlagged);
    setFlaggedReason(isFlagged ? getFlagReasonText(accountData.flaggedReason) : '');

    const notes = accountData?.customerNotes || '';
    setCustomerNotes(notes);
    setInitialCustomerNotes(notes);
    setShowCustomerNotes(Boolean(notes));
  }, []);

  useEffect(() => {
    const role = sessionStorage.getItem('userRole');
    const storedUsername = sessionStorage.getItem('username');
    setUserRole(role);
    setUsername(storedUsername);

    if (appointment.details) {
      // Merge initialFormData with appointment.details to ensure all fields are present
      const details = appointment.details;

      // Convert any Timestamp fields to Date objects
      const convertedDetails = { ...details };

      const dateFields = ['startTime', 'resumeTime', 'pausedTime', 'checkInTime', 'completionTime', 'partsOrderedAt', 'partsReceivedAt', 'partsStatusUpdatedAt']; // List all date fields

      dateFields.forEach(field => {
        if (details[field]) {
          if (details[field].toDate) {
            // If it's a Timestamp object, convert it to Date
            convertedDetails[field] = details[field].toDate();
          } else if (typeof details[field] === 'string' || typeof details[field] === 'number') {
            // If it's a string or number, convert it to Date
            convertedDetails[field] = new Date(details[field]);
          }
        }
      });

      setFormData({ ...initialFormData, ...convertedDetails });
      console.log('Loaded appointment details with converted dates:', convertedDetails);
      setInitialComments(appointment.details.comments || '');

      // Open the private note straight away when there is something to read, so a
      // note left for the next receptionist cannot be missed behind a closed panel.
      setShowPrivateNote(Boolean(convertedDetails.privateNote));

      // Check if the account is flagged when opening an existing appointment
      const vehicleReg = convertedDetails.vehicleReg;
      if (vehicleReg) {
        loadAccountByVehicleReg(vehicleReg).then((accountResult) => {
          applyAccountToForm(accountResult?.data || null);
        });
      } else {
        applyAccountToForm(null);
      }
    } else {
      // New appointment
      setFormData({ ...initialFormData });
      setInitialComments('');
      setShowPrivateNote(false);
      applyAccountToForm(null);
    }
  }, [appointment, loadAccountByVehicleReg, applyAccountToForm]);

  // Load technicians from Firestore
  useEffect(() => {
    const loadTechnicians = async () => {
      try {
        const fetchedTechnicians = await getTechnicians(locationSuffix);
        setTechnicians(fetchedTechnicians);
      } catch (error) {
        console.error('Error loading technicians:', error);
      } finally {
        setLoadingTechnicians(false);
      }
    };

    loadTechnicians();
  }, [locationSuffix]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox separately
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    // Just update the vehicleReg in formData, without performing lookup
    if (name === 'vehicleReg') {
      const updatedValue = value.toUpperCase().replace(/\s+/g, '');
      setFormData((prev) => ({ ...prev, vehicleReg: updatedValue }));
      return;
    }

    // For comments, simply update the comments in formData
    if (name === 'comments') {
      setFormData((prev) => ({
        ...prev,
        comments: value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleExpectedTimeChange = (e) => {
    console.log('Selected time value:', e.target.value);
    setFormData((prev) => ({ ...prev, expectedTime: parseInt(e.target.value) }));
  };

  const handlePartsStatusChange = (nextStatus) => {
    if (userRole !== 'admin') return;

    setFormData((prev) => {
      if (getPartsStatus(prev) === nextStatus) return prev;

      const now = new Date();
      // Keep the ordered stamp once parts are on the way, but drop both stamps if
      // the job goes back to needing parts or not needing them at all.
      const keepsOrderedStamp = nextStatus === PARTS_STATUS.ORDERED || nextStatus === PARTS_STATUS.RECEIVED;
      const orderedAt = keepsOrderedStamp
        ? (prev.partsOrderedAt || (nextStatus === PARTS_STATUS.ORDERED ? now : null))
        : null;

      return {
        ...prev,
        partsStatus: nextStatus,
        partsOrderedAt: orderedAt,
        partsReceivedAt: nextStatus === PARTS_STATUS.RECEIVED ? (prev.partsReceivedAt || now) : null,
        partsStatusUpdatedBy: username || null,
        partsStatusUpdatedAt: now,
      };
    });
  };

  const handleAddTask = () => {
    if (newTask.trim() === '') return;
    setFormData((prev) => ({
      ...prev,
      tasks: [...prev.tasks, { text: newTask, completed: false, completedBy: null }],
      newTasksAdded: prev.inProgress ? true : false,
      completionTime: null, // Reset completionTime when new tasks are added
    }));
    setNewTask('');
  };

  const handleToggleTaskCompletion = (index) => {
    setFormData((prev) => {
      const updatedTasks = [...prev.tasks];
      const currentTask = updatedTasks[index];
      const technician = username; // For display purposes
      const technicianId = prev.currentTechnicianId; // For tracking time

      const currentTime = new Date();
      let timeSpent = 0;

      let resumeTime = prev.resumeTime;
      let startTime = prev.startTime;
      let pausedTime = prev.pausedTime;

      // Convert to Date objects if necessary
      if (resumeTime && !(resumeTime instanceof Date)) {
        resumeTime = new Date(resumeTime);
      }

      if (startTime && !(startTime instanceof Date)) {
        startTime = new Date(startTime);
      }

      // Calculate time spent
      if (resumeTime) {
        timeSpent = Math.floor((currentTime - resumeTime) / 60000);
      } else if (startTime) {
        timeSpent = Math.floor((currentTime - startTime) / 60000);
      } else {
        console.warn('No startTime or resumeTime set. Cannot calculate timeSpent.');
        timeSpent = 0;
      }

      // Check if this is the last incomplete task
      const incompleteTasks = updatedTasks.filter((task) => !task.completed);
      const isLastTask = incompleteTasks.length === 1 && !currentTask.completed;

      // If it's the last task and mileage hasn't been updated, prevent completion
      if (isLastTask && (!prev.mileage || !prev.mileageUpdated)) {
        alert('Please update the Mileage before completing the last task.');
        return prev; // Do not update the state
      }

      currentTask.completed = !currentTask.completed;
      currentTask.completedBy = technician;
      currentTask.timeSpent = timeSpent;

      // Update technician's total time (using technicianId as key)
      const updatedTechnicianTimes = { ...prev.technicianTimes };
      if (technicianId) {
        if (updatedTechnicianTimes[technicianId]) {
          updatedTechnicianTimes[technicianId] += timeSpent;
        } else {
          updatedTechnicianTimes[technicianId] = timeSpent;
        }
      }

      // Check if all tasks are completed
      const allTasksCompleted = updatedTasks.every((task) => task.completed);

      let completionTime = prev.completionTime;
      let inProgress = prev.inProgress;

      if (allTasksCompleted && !completionTime) {
        completionTime = currentTime;
        inProgress = false; // Set inProgress to false when all tasks are completed
        // Reset timers
        startTime = null;
        resumeTime = null;
        pausedTime = null;
      }

      return {
        ...prev,
        tasks: updatedTasks,
        resumeTime: currentTask.completed ? null : currentTime,
        technicianTimes: updatedTechnicianTimes,
        completionTime,
        inProgress,
        startTime,
        pausedTime,
      };
    });
  };

  const handleCheckIn = () => {
    const currentTime = new Date();
    // Find the technician ID for the current username
    const currentTech = technicians.find(t => t.name === username);
    const currentTechId = currentTech ? currentTech.id : null;

    setFormData((prev) => ({
      ...prev,
      inProgress: true,
      startTime: prev.startTime || currentTime, // Keep original startTime if it exists
      resumeTime: currentTime, // Set resumeTime to current time
      checkInTime: prev.checkInTime || currentTime, // Keep original check-in time
      currentTechnicianId: currentTechId, // Store the technician ID
    }));
    onCheckIn(appointment.id);
  };



  const handlePause = () => {
    const pauseTime = new Date();
    setFormData((prev) => {
      const technicianTime = pauseTime - new Date(prev.resumeTime || prev.startTime);
      const updatedTechnicianTimes = { ...prev.technicianTimes };

      // Add the time spent to the current technician's total (using ID as key)
      if (prev.currentTechnicianId) {
        if (updatedTechnicianTimes[prev.currentTechnicianId]) {
          updatedTechnicianTimes[prev.currentTechnicianId] += Math.floor(technicianTime / 60000);
        } else {
          updatedTechnicianTimes[prev.currentTechnicianId] = Math.floor(technicianTime / 60000);
        }
      }

      return {
        ...prev,
        isPaused: true,
        pausedTime: pauseTime,
        technicianTimes: updatedTechnicianTimes,
        resumeTime: null, // Reset resumeTime when paused
      };
    });
  };

  const handleResume = () => {
    const resumeTime = new Date();
    // Find the technician ID for the current username
    const currentTech = technicians.find(t => t.name === username);
    const currentTechId = currentTech ? currentTech.id : null;

    setFormData((prev) => ({
      ...prev,
      isPaused: false,
      resumeTime,
      currentTechnicianId: currentTechId, // Update to the current technician ID
    }));
  };

  const handleDeleteTask = (index) => {
    setFormData((prev) => {
      const updatedTasks = [...prev.tasks];
      updatedTasks.splice(index, 1);
      return { ...prev, tasks: updatedTasks };
    });
  };

  const handleStartEditTask = (index) => {
    setEditingTaskIndex(index);
    setEditingTaskText(formData.tasks[index].text);
  };

  const handleSaveEditTask = (index) => {
    if (editingTaskText.trim() === '') return;
    setFormData((prev) => {
      const updatedTasks = [...prev.tasks];
      updatedTasks[index] = { ...updatedTasks[index], text: editingTaskText.trim() };
      return { ...prev, tasks: updatedTasks };
    });
    setEditingTaskIndex(null);
    setEditingTaskText('');
  };

  const handleCancelEditTask = () => {
    setEditingTaskIndex(null);
    setEditingTaskText('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let formDataToSave = { ...formData };

    if (initialComments !== formData.comments) {
      formDataToSave.newCommentsAdded = true;
    } else if (formData.newCommentsAdded) {
      formDataToSave.newCommentsAdded = false;
    }

    if (onSave) {
      // When saving, ensure techId is set; only clear tech field for existing appointments
      let finalTechId = appointment.techId;
      
      // If we don't have a techId, try to look it up from the tech field (for old appointments)
      if (!finalTechId && appointment.tech) {
        // Look up the technician ID by name
        const techByName = technicians.find(t => t.name === appointment.tech);
        finalTechId = techByName ? techByName.id : appointment.tech;
      }
      
      const appointmentToSave = {
        ...appointment,
        techId: finalTechId, // Use the looked-up or existing techId
        details: formDataToSave,
      };
      
      // Only set tech: undefined for existing appointments to trigger Firestore deletion
      if (appointment.id) {
        appointmentToSave.tech = undefined;
      } else {
        // For new appointments, remove the tech field completely to avoid Firestore errors
        delete appointmentToSave.tech;
      }
      
      onSave(appointmentToSave);
    }

    // Save updated mileage and customer notes to the vehicle's account. Notes are
    // only written when they actually changed, so an account lookup that has not
    // resolved yet can never blank out an existing note.
    const normalizedReg = formData.vehicleReg.trim().toUpperCase().replace(/\s+/g, '');
    if (normalizedReg) {
      const accountUpdates = {};

      if (formData.mileage) {
        accountUpdates.mileage = formData.mileage;
      }
      if (customerNotes !== initialCustomerNotes) {
        accountUpdates.customerNotes = customerNotes.trim();
      }

      if (Object.keys(accountUpdates).length > 0) {
        const accountResult = await loadAccountByVehicleReg(normalizedReg);
        // Fall back to the reg as the doc id so a brand new customer's details
        // are still recorded rather than silently dropped.
        const accountRef = accountResult?.ref || doc(firestore, accountsCollectionName, normalizedReg);
        await setDoc(accountRef, accountUpdates, { merge: true });
      }
    }
  };


  const handleModalOpen = () => {
    setFormData((prev) => ({
      ...prev,
      newTasksAdded: false,
    }));
  };

  const handleDelete = () => {
    if (appointment.id && window.confirm('Are you sure you want to delete this appointment?')) {
      if (onDelete) {
        onDelete(appointment.id);
      }
    }
  };

  const handleReschedule = () => {
    // Open the reschedule modal
    const initialRescheduleTechId = appointment.techId || appointment.tech || '';
    const appointmentDate = appointment.date ? new Date(appointment.date) : new Date();
    const isValidDate = !Number.isNaN(appointmentDate.getTime());
    const toLocalDateString = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const formattedDate = isValidDate ? toLocalDateString(appointmentDate) : toLocalDateString(new Date());

    setNewTechnicianId(initialRescheduleTechId);
    setNewDate(formattedDate);
    setNewTime(getSafeRescheduleTime(
      appointment.startTime,
      getRescheduleTimeOptions(isValidDate ? appointmentDate : new Date())
    ));
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleSubmit = () => {
    if (!newTechnicianId || !newDate || !rescheduleTime) {
      alert('Please select a technician, date, and time to reschedule.');
      return;
    }

    // Parse as a local date; new Date('2026-10-26') is UTC and can shift a day.
    const parsedDate = parseDateInputValue(newDate);
    if (!parsedDate) {
      alert('Please select a valid date.');
      return;
    }

    // Checked against the chosen day's own slots, because lunch - and so which
    // slots exist - differs between the garages and changed on a date.
    if (!isValidRescheduleTime(rescheduleTime, getRescheduleTimeOptions(parsedDate))) {
      alert('Please pick one of the bookable times for that day.');
      return;
    }

    if (isNonWorkingDay(parsedDate)) {
      alert(
        `${parsedDate.toLocaleDateString('en-IE')} is ${describeNonWorkingDay(parsedDate)}. `
        + 'Please choose a working day.'
      );
      return;
    }

    // Format the date to match the original format
    const formattedDate = parsedDate.toDateString(); // Converts to "Wed Sep 25 2024"

    // Create the updated appointment with the new date
    const updatedAppointment = {
      ...appointment,
      techId: newTechnicianId, // Use technician ID instead of name
      date: formattedDate, // Use the formatted date
      startTime: rescheduleTime,
    };

    // Save the updated appointment
    onSave(updatedAppointment);
    setIsRescheduleModalOpen(false);
  };

  const handleVehicleRegKeyDown = async (e) => {
    if (e.key === ' ') {
      e.preventDefault();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent the default action (form submission)

      const updatedValue = formData.vehicleReg.trim().toUpperCase().replace(/\s+/g, '');
      if (updatedValue !== '') {
        const accountResult = await loadAccountByVehicleReg(updatedValue);

        if (accountResult) {
          const accountData = accountResult.data;
          setFormData((prev) => ({
            ...prev,
            vehicleMake: accountData.vehicleMake || '',
            customerName: accountData.customerName || '',
            customerPhone: accountData.customerPhone || '',
            mileage: accountData.mileage || '', // Populate mileage
          }));
          applyAccountToForm(accountData);
          setVehicleRegLookupStatus('Vehicle details loaded.');
        } else {
          // If no match is found, clear the account detail fields
          setFormData((prev) => ({
            ...prev,
            vehicleMake: '',
            customerName: '',
            customerPhone: '',
            mileage: '', // Clear mileage
          }));
          applyAccountToForm(null);
          setVehicleRegLookupStatus('No matching vehicle found.');
        }
      }
    }
  };

  const handleMileageChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      mileage: value,
      mileageUpdated: true, // Set to true when the mileage is updated
    }));
  };

  const handleTaskInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleAddTask(); // Call the function to add the task

      // Refocus the input field
      if (taskInputRef.current) {
        taskInputRef.current.focus();
      }
    }
  };



  useEffect(() => {
    handleModalOpen();
  }, []);

  const rescheduleParsedDate = parseDateInputValue(newDate);
  const rescheduleDateWarning = rescheduleParsedDate && isNonWorkingDay(rescheduleParsedDate)
    ? `${rescheduleParsedDate.toLocaleDateString('en-IE')} is ${describeNonWorkingDay(rescheduleParsedDate)}.`
    : '';

  // Only the slots that exist on the chosen day, so a job cannot be moved onto
  // lunch. Picking a day whose lunch differs re-points the list, and a time that
  // day does not have falls back to its first slot.
  const rescheduleTimeOptions = getRescheduleTimeOptions(rescheduleParsedDate);
  const rescheduleTime = getSafeRescheduleTime(newTime, rescheduleTimeOptions);

  const hasCustomerNotes = customerNotes.trim() !== '';
  const hasPrivateNote = (formData.privateNote || '').trim() !== '';
  const partsStatus = getPartsStatus(formData);
  const partsOrderedAtText = formatPartsTimestamp(formData.partsOrderedAt);
  const partsReceivedAtText = formatPartsTimestamp(formData.partsReceivedAt);
  const partsUpdatedAtText = formatPartsTimestamp(formData.partsStatusUpdatedAt);

  const partsStatusMeta = (() => {
    const entries = [];
    if (partsOrderedAtText) entries.push(`Ordered ${partsOrderedAtText}`);
    if (partsReceivedAtText) entries.push(`Received ${partsReceivedAtText}`);
    if (entries.length === 0 && partsStatus !== PARTS_STATUS.NONE && partsUpdatedAtText) {
      entries.push(`Updated ${partsUpdatedAtText}`);
    }
    if (entries.length > 0 && formData.partsStatusUpdatedBy) {
      entries.push(`by ${formData.partsStatusUpdatedBy}`);
    }
    return entries.join(' · ');
  })();

  return (
    <div className="modal">
      <div className="modal-content">
        <span className="close" onClick={onClose}>&times;</span>
        <div className="modal-header">
          {!appointment.id && <h2>New Appointment at {startTime}</h2>}
          {appointment.id && <h2>Edit Appointment at {displayTime}</h2>}
          <div className="icon-group">
            <ReactToPrint
              trigger={() => {
                return <FaPrint className="print-icon" title="Print Job Card" />;
              }}
              content={() => componentRef.current}
              onBeforePrint={() => console.log('Before print')}
              onAfterPrint={() => console.log('After print')}
            />
            {userRole === 'admin' && (
              <FaExchangeAlt
                className="reschedule-icon"
                title="Reschedule Appointment"
                onClick={handleReschedule}
              />
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="appointment-form">
          {/* Display check-in and completion times for admins under the header and above the Vehicle Reg field */}
          {userRole === 'admin' && (
            <div className="admin-info">
              {formData.checkInTime && (
                <p>Checked in at: {formData.checkInTime.toLocaleString()}</p>
              )}
              {formData.completionTime && (
                <p>Completed at: {formData.completionTime.toLocaleString()}</p>
              )}
            </div>
          )}

          {isFlaggedAccount && (
            <div className="flagged-account-warning">
              <span className="flagged-account-warning__icon">⚑</span>
              <div>
                <strong>Flagged Account</strong>
                <p>
                  {flaggedReason
                    ? `This account was flagged because of ${flaggedReason}.`
                    : 'This vehicle registration has been flagged. Please proceed with caution.'}
                </p>
              </div>
            </div>
          )}

          <div className="left-section">
            <label>
              Vehicle Reg:
              <input
                type="text"
                name="vehicleReg"
                value={formData.vehicleReg}
                onChange={handleChange}
                onKeyDown={handleVehicleRegKeyDown}
                required
              />
            </label>
            {vehicleRegLookupStatus && <p>{vehicleRegLookupStatus}</p>}
            <label>
              Vehicle Make:
              <input
                type="text"
                name="vehicleMake"
                value={formData.vehicleMake}
                onChange={handleChange}
              />
            </label>
            <label>
              Mileage:
              <input
                type="text"
                name="mileage"
                value={formData.mileage}
                onChange={handleMileageChange}
              />
            </label>
            <label>
              Customer Name:
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
              />
            </label>
            <label>
              Customer Phone:
              <input
                type="text"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleChange}
                required
              />
            </label>

            <div className={`customer-notes-section${hasCustomerNotes ? ' customer-notes-section--filled' : ''}`}>
              <button
                type="button"
                className="customer-notes-toggle"
                onClick={() => setShowCustomerNotes((prev) => !prev)}
              >
                <FaInfoCircle />
                Customer Notes
                {hasCustomerNotes && !showCustomerNotes && (
                  <span className="customer-notes-dot" title="This account has notes" />
                )}
                <span className="customer-notes-chevron">{showCustomerNotes ? '▲' : '▼'}</span>
              </button>
              {showCustomerNotes && (
                <>
                  <textarea
                    className="customer-notes-textarea"
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    readOnly={userRole !== 'admin'}
                    placeholder="Extra contact or collection details, e.g. partner collects the car, company contacts..."
                  />
                  <p className="customer-notes-hint">
                    Saved to the account for {formData.vehicleReg || 'this vehicle'} and shown on every future booking.
                  </p>
                </>
              )}
            </div>

            {/* Updated Expected Time and Needs Validation */}
            <div className="time-validation-section">
              <label className="expected-time-label">
                Expected Time:
              </label>
              <div className="time-validation-controls">
                <select
                  name="expectedTime"
                  value={formData.expectedTime}
                  onChange={handleExpectedTimeChange}
                  required={!formData.needsValidation} // Not required if needs validation
                >
                  {timeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <label className="validation-checkbox">
                  <input
                    type="checkbox"
                    name="needsValidation"
                    checked={formData.needsValidation}
                    onChange={handleChange}
                  />
                  Time Needs Confirmation
                </label>
              </div>
            </div>

            <div className={`parts-status-section parts-status-section--${partsStatus}`}>
              <label className="parts-status-label">Parts:</label>
              <div className="parts-status-options" role="group" aria-label="Parts status">
                {PARTS_STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`parts-status-button parts-status-button--${option.value}${partsStatus === option.value ? ' is-active' : ''}`}
                    onClick={() => handlePartsStatusChange(option.value)}
                    disabled={userRole !== 'admin'}
                    aria-pressed={partsStatus === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {partsStatusMeta && <p className="parts-status-meta">{partsStatusMeta}</p>}
            </div>
          </div>

          <div className="right-section">
            <div className="todo-list-section">
              <label>To-Do List:</label>
              <ul className="todo-list">
                {formData.tasks.map((task, index) => (
                  <li key={index} className={`task-item ${task.completed ? 'completed' : ''}`}>
                    <span className="task-circle" onClick={() => handleToggleTaskCompletion(index)}>
                      {task.completed ? <FaCheckCircle /> : <FaCircle />}
                    </span>
                    {editingTaskIndex === index ? (
                      <>
                        <input
                          className="task-edit-input"
                          type="text"
                          value={editingTaskText}
                          onChange={(e) => setEditingTaskText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEditTask(index);
                            if (e.key === 'Escape') handleCancelEditTask();
                          }}
                          autoFocus
                        />
                        <button type="button" className="task-save-btn" title="Save" onClick={() => handleSaveEditTask(index)}><FaCheck /></button>
                        <button type="button" className="task-cancel-btn" title="Cancel" onClick={handleCancelEditTask}><FaTimes /></button>
                      </>
                    ) : (
                      <>
                        <span className="task-text">{task.text}</span>
                        {task.completed && (
                          <span className="completed-by">
                            (Completed by: {task.completedBy} in {task.timeSpent}m)
                          </span>
                        )}
                        {!task.completed && (
                          <button type="button" className="task-edit-btn" title="Edit task" onClick={() => handleStartEditTask(index)}><FaPencilAlt /></button>
                        )}
                        <button type="button" className="task-delete-btn" title="Delete task" onClick={() => handleDeleteTask(index)}><FaTrash /></button>
                      </>
                    )}
                  </li>
                ))}
              </ul>

              <div className="new-task-input">
                <input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Add a new task"
                  ref={taskInputRef} // Add this
                  onKeyDown={handleTaskInputKeyDown} // Add this
                />
                <button type="button" onClick={handleAddTask}>Add Task</button>
              </div>
            </div>

            <div className="comments-section">
              <label>Comments:</label>
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                placeholder="Add any additional notes here..."
              />
            </div>

            {userRole === 'admin' && (
              <div className={`private-note-section${hasPrivateNote ? ' private-note-section--filled' : ''}`}>
                <button
                  type="button"
                  className="private-note-toggle"
                  onClick={() => setShowPrivateNote((prev) => !prev)}
                >
                  <FaLock />
                  Private Note
                  {hasPrivateNote && !showPrivateNote && (
                    <span className="private-note-dot" title="This appointment has a private note" />
                  )}
                  <span className="private-note-chevron">{showPrivateNote ? '▲' : '▼'}</span>
                </button>
                {showPrivateNote && (
                  <textarea
                    className="private-note-textarea"
                    name="privateNote"
                    value={formData.privateNote || ''}
                    onChange={handleChange}
                    placeholder="Add a private admin note (only visible to admins)..."
                  />
                )}
              </div>
            )}
          </div>

          <div className="button-group">
            {!formData.inProgress && (
              <button type="button" className="checkin-button" onClick={handleCheckIn}>
                Check In
              </button>
            )}
            {formData.inProgress && !formData.isPaused && (
              <button type="button" className="pause-button" onClick={handlePause}>
                Pause Appointment
              </button>
            )}
            {formData.inProgress && formData.isPaused && (
              <button type="button" className="resume-button" onClick={handleResume}>
                Resume Appointment
              </button>
            )}
            <button type="submit" disabled={userRole !== 'admin' && !appointment.id}>
              Save Appointment
            </button>
          </div>


          {/* Only show the delete button if the user is an admin */}
          {userRole === 'admin' && appointment.id && (
            <button
              type="button"
              className="delete-button"
              onClick={handleDelete}
            >
              Delete Appointment
            </button>
          )}
        </form>

        {isRescheduleModalOpen && (
          <div className="reschedule-modal">
            <div className="reschedule-content">
              <h2>Reschedule Appointment</h2>
              <label>
                Technician:
                <select
                  value={newTechnicianId}
                  onChange={(e) => setNewTechnicianId(e.target.value)}
                  disabled={loadingTechnicians}
                >
                  <option value="">Select Technician</option>
                  {technicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>{tech.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Date:
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </label>
              {rescheduleDateWarning && (
                <p className="reschedule-date-warning">{rescheduleDateWarning}</p>
              )}
              <label>
                Time:
                <select
                  value={rescheduleTime}
                  onChange={(e) => setNewTime(e.target.value)}
                >
                  {rescheduleTimeOptions.map((timeOption) => (
                    <option key={timeOption} value={timeOption}>{timeOption}</option>
                  ))}
                </select>
              </label>
              <button onClick={handleRescheduleSubmit} disabled={Boolean(rescheduleDateWarning)}>
                Reschedule
              </button>
              <button onClick={() => setIsRescheduleModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
        {/* Hidden printable area */}
        <div id="printable-area" style={{ display: 'none' }}>
          <PrintableJobCard ref={componentRef} appointment={appointment || { details: {} }} />
        </div>
      </div>
    </div>
  );
}

export default AppointmentModal;
